// Public (unauthenticated) floor plan landing page.
// Served at /fp/:id — recipients of emails land here when the
// "Link floor plans to LocateNow page" toggle is on in the send dialog.
//
// The layout mirrors _mockup/index.html (UNIFI-style, LocateNow brand).
// Data comes from GET /api/public/floorplan/:id (no auth).

import React from "react";

// Reuse the same API base the authed app uses (REACT_APP_API_URL already
// includes the /api suffix — see lib/api.ts). This ensures production
// recipients hit the same backend as the rest of the app.
const API_BASE: string = (
  process.env.REACT_APP_API_URL || "http://localhost:8000/api"
).replace(/\/$/, "");

type Unit = {
  unit_number: string;
  current_price: number | null;
  available_date: string | null;
  square_feet: number | null;
  status: string | null;
};

type FloorPlan = {
  id: string;
  model_name: string;
  bedrooms: number | null;
  bathrooms: number | null;
  square_feet: number | null;
  min_price: number | null;
  image_url: string | null;
  updated_at: string | null;
};

type Property = {
  id: string;
  name: string | null;
  neighborhood: string | null;
  city: string | null;
  state: string | null;
  property_website: string | null;
  pets_allowed: boolean | null;
  last_scraped: string | null;
};

type ApiResponse = {
  floor_plan: FloorPlan;
  property: Property;
  units: Unit[];
};

function formatPrice(n: number | null | undefined): string {
  if (n == null) return "";
  return "$" + Math.round(n).toLocaleString();
}

function formatDate(iso: string | null): string {
  if (!iso) return "";
  // Date-only strings (YYYY-MM-DD, from Postgres `date` columns) are parsed
  // by `new Date()` as UTC midnight. In negative-offset zones (e.g. US/ET)
  // that renders as the prior day. Parse those as local dates explicitly so
  // Available Jun 5 doesn't show as Available Jun 4 for east-coast readers.
  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  const d = dateOnlyMatch
    ? new Date(Number(dateOnlyMatch[1]), Number(dateOnlyMatch[2]) - 1, Number(dateOnlyMatch[3]))
    : new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

// Mirrors emailService.sanitizeUrl: accept only absolute http(s) URLs; fall
// back to normalizing a missing scheme (scraped property_website values are
// sometimes bare domains). Returns null if we shouldn't render the link.
function sanitizeExternalUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = String(raw).trim();
  if (!trimmed) return null;
  const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const u = new URL(withScheme);
    if (u.protocol !== "http:" && u.protocol !== "https:") return null;
    return u.toString();
  } catch {
    return null;
  }
}

function getFloorPlanIdFromPath(): string | null {
  const m = window.location.pathname.match(/^\/fp\/([^/?#]+)/);
  return m ? m[1] : null;
}

const FloorplanPublicPage: React.FC = () => {
  const [data, setData] = React.useState<ApiResponse | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const id = getFloorPlanIdFromPath();
    if (!id) {
      setError("Invalid floor plan link.");
      setLoading(false);
      return;
    }
    fetch(`${API_BASE}/public/floorplan/${encodeURIComponent(id)}`)
      .then(async (r) => {
        if (!r.ok) throw new Error(r.status === 404 ? "Floor plan not found" : "Unable to load floor plan");
        return r.json() as Promise<ApiResponse>;
      })
      .then((json) => setData(json))
      .catch((e) => setError(e.message || "Unable to load floor plan"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Shell><Center>Loading…</Center></Shell>;
  if (error || !data) return <Shell><Center>{error || "Not found"}</Center></Shell>;

  const { floor_plan: fp, property: prop, units } = data;
  const propertyWebsite = sanitizeExternalUrl(prop.property_website);
  // Only surface actually-available units in the availability section.
  // Unavailable/leased rows are dropped — previously they rendered as "Soon"
  // under "Available in this floor plan", which misrepresented live inventory.
  const availableUnits = units.filter((u) => (u.status || "").toLowerCase() === "available");
  const orderedUnits = availableUnits;
  // Prefer the cheapest *available* unit price so "Starting at" matches the
  // unit list the reader is seeing. `fp.min_price` can be stale or computed
  // from unavailable inventory, so only use it as a fallback when no
  // available unit has a price.
  const availablePrices = availableUnits
    .map((u) => u.current_price)
    .filter((p): p is number => typeof p === "number" && Number.isFinite(p));
  const startingPrice = availablePrices.length > 0
    ? Math.min(...availablePrices)
    : fp.min_price ?? null;
  const cityState = [prop.city, prop.state].filter(Boolean).join(", ");
  const location = [prop.neighborhood, cityState].filter(Boolean).join(" · ");
  const subheading = [
    fp.bedrooms != null ? `${fp.bedrooms} Bed` : null,
    fp.bathrooms != null ? `${fp.bathrooms} Bath` : null,
  ].filter(Boolean).join(" / ");

  return (
    <Shell>
      <div className="fp-page" style={styles.page}>
        <div style={styles.crumbs}>
          {prop.name || "Property"}
          {subheading && (
            <>
              {" · "}
              <span style={{ color: "#111827" }}>{fp.model_name} — {subheading}</span>
            </>
          )}
        </div>

        <div className="fp-grid" style={styles.grid}>
          {/* LEFT: hero */}
          <section style={styles.card}>
            <div style={styles.hero}>
              <div style={styles.modelRow}>
                <div style={styles.modelName}>{fp.model_name}</div>
                <div style={styles.modelSub}>Floor Plan</div>
              </div>
              <div style={styles.specs}>
                {fp.bedrooms != null && <Spec label="Beds" value={fp.bedrooms} />}
                {fp.bathrooms != null && <Spec label="Baths" value={fp.bathrooms} />}
                {fp.square_feet != null && <Spec label="Sq ft" value={fp.square_feet.toLocaleString()} />}
                {startingPrice != null && (
                  <Spec label="Starting at" value={`${formatPrice(startingPrice)}/mo`} />
                )}
              </div>
            </div>
            <div className="fp-image-wrap" style={styles.imageWrap}>
              {fp.image_url ? (
                <img src={fp.image_url} alt={fp.model_name} style={styles.image} />
              ) : (
                <div style={styles.imagePlaceholder}>Floor plan image unavailable</div>
              )}
            </div>
            <div style={styles.heroFoot}>
              <div style={styles.smallMuted}>
                {prop.name ? `Image sourced from ${prop.name}` : "Floor plan image"}
                {fp.updated_at && ` · Updated ${formatDate(fp.updated_at)}`}
              </div>
              {propertyWebsite && (
                <a href={propertyWebsite} target="_blank" rel="noopener noreferrer" style={styles.btnPrimary}>
                  Visit Property Website →
                </a>
              )}
            </div>
          </section>

          {/* RIGHT: property + units */}
          <aside>
            <section style={{ ...styles.card, ...styles.propCard }}>
              <div style={styles.sectionTitle}>Property</div>
              <div style={styles.propName}>{prop.name || "Property"}</div>
              {location && <div style={styles.propHood}>{location}</div>}
              <div style={styles.propMeta}>
                {availableUnits.length > 0 && (
                  <span>
                    <b style={{ color: "#111827" }}>{availableUnits.length}</b> unit
                    {availableUnits.length === 1 ? "" : "s"} available
                  </span>
                )}
                {prop.pets_allowed === true && <span>Pet friendly</span>}
              </div>
              {propertyWebsite && (
                <div style={{ marginTop: 14 }}>
                  <a href={propertyWebsite} target="_blank" rel="noopener noreferrer" style={styles.btnPrimary}>
                    Visit Website →
                  </a>
                </div>
              )}
            </section>

            {orderedUnits.length > 0 && (
              <section style={{ ...styles.card, ...styles.unitsCard }}>
                <div style={styles.unitsHead}>
                  <div style={{ ...styles.sectionTitle, margin: 0 }}>Available in this floor plan</div>
                  <div style={styles.unitsCount}>
                    <b style={{ color: "#111827" }}>{orderedUnits.length}</b> unit{orderedUnits.length === 1 ? "" : "s"}
                  </div>
                </div>
                {orderedUnits.map((u) => (
                  <div key={u.unit_number} style={styles.unit}>
                    <div style={styles.unitNo}>Unit {u.unit_number}</div>
                    <div style={styles.unitPrice}>{formatPrice(u.current_price)}</div>
                    <div style={styles.unitSub}>
                      {u.square_feet != null && `${u.square_feet.toLocaleString()} sq ft · `}
                      {u.available_date ? `Available ${formatDate(u.available_date)}` : "Available now"}
                    </div>
                    <div style={{ ...styles.unitPill, ...styles.pillNow }}>Available</div>
                  </div>
                ))}
              </section>
            )}
          </aside>
        </div>

        <div style={styles.footer}>
          Shared via <span style={{ color: "#005dff", fontWeight: 600 }}>LocateNow</span> · Prices and availability may vary. Confirm current details on the property website.
        </div>
      </div>
    </Shell>
  );
};

// The grid template lives in a stylesheet (not inline) so the mobile
// media query can override it without an `!important` race against inline
// styles. Most email clicks are mobile; below the tablet breakpoint we
// collapse to one column and tighten page padding.
const RESPONSIVE_CSS = `
  .fp-grid { display: grid; grid-template-columns: 1.35fr 1fr; gap: 24px; }
  @media (max-width: 900px) {
    .fp-grid { grid-template-columns: 1fr; }
    .fp-page { padding: 20px 16px; }
    .fp-image-wrap { padding: 20px; min-height: 280px; }
  }
`;

const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={styles.root}>
    <style>{RESPONSIVE_CSS}</style>
    <header style={styles.topbar}>
      <div style={styles.brand}>
        <div style={styles.brandDot} />
        <div style={styles.brandName}>LocateNow</div>
        <div style={styles.brandTag}>Floor Plan</div>
      </div>
    </header>
    {children}
  </div>
);

const Center: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ padding: "80px 20px", textAlign: "center", color: "#6b7280" }}>{children}</div>
);

const Spec: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div style={styles.spec}>
    <span style={styles.specLbl}>{label}</span>
    <b style={{ fontWeight: 600 }}>{value}</b>
  </div>
);

const styles: Record<string, React.CSSProperties> = {
  root: {
    background: "#f7f8fb",
    minHeight: "100vh",
    fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    color: "#111827",
    fontSize: 15,
    lineHeight: 1.5,
    WebkitFontSmoothing: "antialiased",
  },
  topbar: {
    background: "#fff",
    borderBottom: "1px solid #e5e7eb",
    padding: "14px 28px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  brand: { display: "flex", alignItems: "center", gap: 10, fontWeight: 700, color: "#0a1e3f" },
  brandDot: { width: 22, height: 22, borderRadius: 6, background: "linear-gradient(135deg, #005dff 0%, #3b82f6 100%)" },
  brandName: { fontSize: 17 },
  brandTag: {
    fontSize: 11, color: "#6b7280", fontWeight: 500,
    borderLeft: "1px solid #e5e7eb", paddingLeft: 10, marginLeft: 4,
    letterSpacing: ".5px", textTransform: "uppercase" as const,
  },
  page: { maxWidth: 1120, margin: "0 auto", padding: 28 },
  crumbs: { fontSize: 12, color: "#6b7280", marginBottom: 14, letterSpacing: ".2px" },
  // `grid` layout lives in the responsive stylesheet above — inline styles
  // can't express media queries. Keep this entry empty so TS/editors
  // still see the key if anything references styles.grid.
  grid: {},
  card: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, overflow: "hidden" },
  hero: { padding: "20px 22px 8px 22px" },
  modelRow: { display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" as const },
  modelName: { fontSize: 28, fontWeight: 700, letterSpacing: "-0.4px" },
  modelSub: { fontSize: 14, color: "#6b7280" },
  specs: { display: "flex", gap: 22, marginTop: 10, flexWrap: "wrap" as const },
  spec: { fontSize: 13 },
  specLbl: { color: "#6b7280", fontWeight: 500, marginRight: 6 },
  imageWrap: {
    background: "#fafbfc",
    borderTop: "1px solid #e5e7eb",
    borderBottom: "1px solid #e5e7eb",
    padding: 32,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 420,
  },
  image: { maxWidth: "100%", maxHeight: 520, objectFit: "contain" as const },
  imagePlaceholder: { color: "#9ca3af", fontSize: 13 },
  heroFoot: {
    padding: "14px 22px 18px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap" as const,
  },
  smallMuted: { fontSize: 12, color: "#6b7280" },
  btnPrimary: {
    display: "inline-flex", alignItems: "center", gap: 8,
    background: "#005dff", color: "#fff", fontWeight: 600, fontSize: 13,
    padding: "10px 16px", borderRadius: 8, textDecoration: "none",
    boxShadow: "0 1px 2px rgba(0, 93, 255, .25)",
  },
  sectionTitle: {
    fontSize: 12, letterSpacing: ".6px", textTransform: "uppercase" as const,
    color: "#6b7280", fontWeight: 600, margin: "0 0 10px",
  },
  propCard: { padding: "20px 22px" },
  propName: { fontSize: 20, fontWeight: 700, letterSpacing: "-0.2px" },
  propHood: { fontSize: 13, color: "#6b7280", marginTop: 2 },
  propMeta: { display: "flex", gap: 14, fontSize: 12, color: "#6b7280", marginTop: 10, flexWrap: "wrap" as const },
  unitsCard: { padding: "20px 22px", marginTop: 16 },
  unitsHead: { display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 },
  unitsCount: { fontSize: 13, color: "#6b7280" },
  unit: {
    display: "grid", gridTemplateColumns: "1fr auto", gap: "6px 14px",
    padding: "14px 0", borderTop: "1px solid #e5e7eb",
  },
  unitNo: { fontWeight: 600, fontSize: 14 },
  unitPrice: { fontWeight: 700, fontSize: 16, letterSpacing: "-0.2px", textAlign: "right" as const },
  unitSub: { fontSize: 12, color: "#6b7280", gridColumn: "1 / 2" },
  unitPill: {
    fontSize: 11, fontWeight: 600, padding: "3px 8px", borderRadius: 999,
    gridColumn: "2 / 3", justifySelf: "end" as const, alignSelf: "start" as const,
  },
  pillNow: { background: "#ecfdf5", color: "#059669" },
  pillSoon: { background: "#fff7ed", color: "#b45309" },
  footer: { textAlign: "center" as const, color: "#6b7280", fontSize: 12, margin: "28px 0 12px" },
};

export default FloorplanPublicPage;
